/**
 * @module services/zotero/itemService
 * @description Item CRUD operations for Zotero API
 */

import { api } from '@/config';
import { apiClient } from './apiClient';
import { extractErrorMessage } from './helpers';
import type { PaginatedResponse } from './types';
import type {
  Item,
  ItemData,
  CreateItemInput,
  UpdateItemInput,
  LibraryType,
  GetItemsOptions,
  ServiceResult,
} from '@types';
import { buildLibraryEndpoint } from '@types';

// =============================================================================
// ITEM SERVICE
// =============================================================================

/**
 * Get items for a library
 */
export async function getItems(
  libraryId: number,
  libraryType: LibraryType,
  options: GetItemsOptions = {}
): Promise<ServiceResult<PaginatedResponse<Item>>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const {
      collectionKey,
      q,
      qmode = 'titleCreatorYear',
      itemType,
      sort = 'dateModified',
      direction = 'desc',
      limit = api.defaultPageSize,
      start = 0,
      signal,
    } = options;

    let path = `${endpoint}/items`;
    if (collectionKey) {
      path = `${endpoint}/collections/${collectionKey}/items`;
    }

    // Zotero API: "-attachment || note" excludes attachments but keeps notes visible
    // The "|| note" part might be for handling edge cases in Zotero's filter logic
    const params: Record<string, string | number | boolean | undefined> = {
      itemType: itemType || '-attachment || note',
      sort,
      direction,
      limit,
      start,
    };

    if (q) {
      params.q = q;
      params.qmode = qmode;
    }

    const response = await apiClient.request<Item[]>({
      method: 'GET',
      path,
      params,
      signal,
    });

    // Filter out item types that shouldn't appear in the main list
    // The API filter doesn't reliably exclude all types, so we filter client-side
    const excludedTypes = new Set(['attachment', 'note', 'annotation']);
    const filteredData = (response.data || []).filter(
      (item) => !excludedTypes.has(item.data?.itemType)
    );

    // If totalResults is 0 but we have data, use a fallback
    // This handles cases where the API doesn't return Total-Results header
    const itemsReceived = filteredData.length;
    let totalResults = response.totalResults || 0;

    // Adjust totalResults to account for filtered items
    const itemsFiltered = (response.data?.length || 0) - itemsReceived;
    if (itemsFiltered > 0) {
      totalResults = Math.max(0, totalResults - itemsFiltered);
    }

    if (totalResults === 0 && itemsReceived > 0) {
      // If we received exactly the limit, there are likely more items
      totalResults = itemsReceived === limit ? itemsReceived + 1 : itemsReceived;
    }

    return {
      success: true,
      data: {
        data: filteredData,
        totalResults,
        links: {
          self: '',
          ...response.links,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get items',
    };
  }
}

/**
 * Get a single item
 */
export async function getItem(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string
): Promise<ServiceResult<Item>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const response = await apiClient.request<Item>({
      method: 'GET',
      path: `${endpoint}/items/${itemKey}`,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get item',
    };
  }
}

/**
 * Create an item
 */
export async function createItem(
  libraryId: number,
  libraryType: LibraryType,
  input: CreateItemInput
): Promise<ServiceResult<Item>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const response = await apiClient.request<{
      successful: Record<string, Item>;
      failed: Record<string, { code: number; message: string }>;
    }>({
      method: 'POST',
      path: `${endpoint}/items`,
      body: [input],
    });

    const created = Object.values(response.data?.successful || {})[0];
    if (!created) {
      const failed = Object.values(response.data?.failed || {})[0];
      return {
        success: false,
        error: failed?.message || 'Item creation failed',
      };
    }

    return { success: true, data: created };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create item',
    };
  }
}

/**
 * Update an item
 */
export async function updateItem(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string,
  input: UpdateItemInput
): Promise<ServiceResult<Item>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    // Get current version
    const current = await getItem(libraryId, libraryType, itemKey);
    if (!current.success) {
      return current;
    }

    const response = await apiClient.request<Item>({
      method: 'PATCH',
      path: `${endpoint}/items/${itemKey}`,
      headers: {
        'If-Unmodified-Since-Version': String(current.data.version),
      },
      body: input,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update item',
    };
  }
}

/**
 * Delete an item (move to trash)
 */
export async function deleteItem(
  libraryId: number,
  libraryType: LibraryType,
  itemKey: string
): Promise<ServiceResult<void>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    // Get current version
    const current = await getItem(libraryId, libraryType, itemKey);
    if (!current.success) {
      return { success: false, error: current.error };
    }

    await apiClient.request<void>({
      method: 'DELETE',
      path: `${endpoint}/items/${itemKey}`,
      headers: {
        'If-Unmodified-Since-Version': String(current.data.version),
      },
    });

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete item',
    };
  }
}

/**
 * Delete multiple items
 */
export async function deleteItems(
  libraryId: number,
  libraryType: LibraryType,
  itemKeys: string[]
): Promise<ServiceResult<void>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);

    await apiClient.request<void>({
      method: 'DELETE',
      path: `${endpoint}/items`,
      params: {
        itemKey: itemKeys.join(','),
      },
    });

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete items',
    };
  }
}

// =============================================================================
// TRASH
// =============================================================================

/**
 * Get trash items
 */
export async function getTrashItems(
  libraryId: number,
  libraryType: LibraryType,
  options: GetItemsOptions = {}
): Promise<ServiceResult<PaginatedResponse<Item>>> {
  try {
    const endpoint = buildLibraryEndpoint(libraryId, libraryType);
    const { sort = 'dateModified', direction = 'desc', limit = 50, start = 0, signal } = options;

    const response = await apiClient.request<Item[]>({
      method: 'GET',
      path: `${endpoint}/items/trash`,
      params: {
        sort,
        direction,
        limit,
        start,
      },
      signal,
    });

    // If totalResults is 0 but we have data, use a fallback
    const itemsReceived = response.data?.length || 0;
    let totalResults = response.totalResults || 0;
    if (totalResults === 0 && itemsReceived > 0) {
      totalResults = itemsReceived === limit ? itemsReceived + 1 : itemsReceived;
    }

    return {
      success: true,
      data: {
        data: response.data || [],
        totalResults,
        links: {
          self: '',
          ...response.links,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get trash items',
    };
  }
}

/**
 * Empty trash
 */
export async function emptyTrash(
  libraryId: number,
  libraryType: LibraryType
): Promise<ServiceResult<void>> {
  try {
    // Get all trash items first
    const trashResult = await getTrashItems(libraryId, libraryType, { limit: 100 });
    if (!trashResult.success) {
      return { success: false, error: trashResult.error };
    }

    if (trashResult.data.data.length === 0) {
      return { success: true, data: undefined };
    }

    const itemKeys = trashResult.data.data.map((item) => item.key);
    return deleteItems(libraryId, libraryType, itemKeys);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to empty trash',
    };
  }
}

// =============================================================================
// SEARCH
// =============================================================================

/**
 * Search items
 */
export async function searchItems(
  libraryId: number,
  libraryType: LibraryType,
  query: string,
  options: Omit<GetItemsOptions, 'q'> = {}
): Promise<ServiceResult<PaginatedResponse<Item>>> {
  return getItems(libraryId, libraryType, {
    ...options,
    q: query,
  });
}

// =============================================================================
// ITEM TEMPLATES
// =============================================================================

/**
 * Get item template for a type
 */
export async function getItemTemplate(itemType: string): Promise<ServiceResult<Partial<ItemData>>> {
  try {
    const response = await fetch(
      `${api.zoteroBaseUrl}/items/new?itemType=${encodeURIComponent(itemType)}`,
      {
        headers: {
          'Zotero-API-Version': String(api.version),
        },
      }
    );

    if (!response.ok) {
      const message = await extractErrorMessage(response);
      return { success: false, error: message };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get item template',
    };
  }
}
